import log from 'loglevel'
import React from 'react'
import {
  BooleanInput,
  Form,
  FormDataConsumer,
  type InputProps,
  Loading,
  NumberInput,
  type RaRecord,
  required as requiredApi,
  SelectInput,
  useDataProvider,
  useGetList,
  useInput,
  useNotify,
  useTranslate
} from 'react-admin'
import { Box, Button, Checkbox, Grid, Typography } from '@mui/material'
import {
  compact,
  endsWith,
  filter,
  find,
  flatten,
  flow,
  groupBy as groupByApi,
  head,
  includes,
  isBoolean,
  isEmpty,
  isNil,
  isNumber,
  keys,
  last,
  map,
  reduce,
  replace,
  size,
  slice,
  split,
  toNumber,
  uniq
} from 'lodash/fp'
import { DateInput, TimeInput } from 'components/Util'
// import { schema } from './schema'
import { useFormContext, useWatch } from 'react-hook-form'
import { type ConfigProps } from 'components/Dashboard'
import { getAllData } from 'components/DataProvider/functions'
import {
  format,
  isAfter,
  isBefore,
  parseISO,
  startOfYear,
  subDays
} from 'date-fns/fp'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import { assign } from 'lodash'
import { type Mapping } from '../../../../cdk/bapp/lib/lambda-api/src/types'
import { MissingsDialog } from 'components/Download'
import { type PartnerUser } from '@mzc-watchover/adminapp-appsync'
import { z } from 'zod'

log.setLevel((process.env['LOG_LEVEL'] as log.LogLevelDesc) ?? log.levels.DEBUG)

const FormContent: React.FC<{
  partner: string
  config: Record<string, any>
  targetValues: any
}> = ({ partner, config, targetValues }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const { AllData: configAllData, Limits: configLimits } = config
  const { durations, conditions, targets, mappings } = configAllData
  const [disableTargets, setDisableTargets] = React.useState(false)
  const [disableEnd, setDisableEnd] = React.useState({
    yearEnd: true,
    monthEnd: true,
    dateEnd: true
  })
  const [rankCondition, setRankCondition] = React.useState<string | undefined>(
    undefined
  )
  const [open, setOpen] = React.useState<boolean>(false)
  const [disableDownload, setDisableDownload] = React.useState(false)
  const [missings, setMissings] = React.useState<
    Array<Partial<PartnerUser> & { from: string }>
  >([])
  const [params, setParams] = React.useState<
    Record<string, string> | undefined
  >(undefined)
  const dataProvider = useDataProvider()
  const { watch, setValue, getValues } = useFormContext()
  const onClickClear = (): void => {
    map((item: { key: string }) => {
      setValue(item.key, undefined)
    })(targets.items)
  }
  const onAllSelect = (
    event: React.ChangeEvent<HTMLInputElement> | RaRecord
  ): void => {
    setDisableTargets(event.target.checked)
  }
  const onChangeDuration = (source: string, value: string): void => {
    const item = find((item: { items: Array<{ key: string }> }) =>
      includes(source)(map((item: { key: string }) => item.key)(item.items))
    )(durations)
    if (isNil(item)) return
    const sourceItem = find(
      (item: { key: string; exclusive: string[] | undefined | null }) =>
        item.key === source
    )(item.items)
    if (isNil(sourceItem)) return
    const { exclusive } = sourceItem
    const disabled = JSON.parse(JSON.stringify(disableEnd))
    map((source: string) => {
      setValue(source, '')
      if (!endsWith('End')(source)) {
        disabled[`${source}End`] = true
      }
    })(exclusive)
    if (!endsWith('End')(source)) {
      disabled[`${source}End`] = isEmpty(value)
    }
    setDisableEnd(disabled)
  }
  const checkTargets = async (
    params: Record<string, string>
  ): Promise<Mapping[]> => {
    const { year, date, month } = params
    const start = date ?? month ?? year
    if (isNil(start)) return []
    const { targets } = params
    const filterValue = reduce(
      (result: Record<string, string>, key: string) => {
        if (!isNil(params[key])) {
          return { ...result, [key]: params[key] }
        } else {
          return result
        }
      },
      {}
    )(targets)
    const { total } = await dataProvider
      .getList('mappings', {
        pagination: { perPage: 1, page: 1 },
        sort: { field: 'id', order: 'ASC' },
        filter: filterValue
      })
      .catch((error) => {
        log.error({ error })
        return { total: undefined }
      })
    if (isNil(total)) return []
    const { data } = await dataProvider
      .getList('mappings', {
        pagination: { perPage: total, page: 1 },
        sort: { field: 'id', order: 'ASC' },
        filter: filterValue
      })
      .catch((error) => {
        log.error({ error })
        return { data: [] }
      })
    const missings = filter((item: Mapping) =>
      isAfter(parseISO(start))(parseISO(item.from))
    )(data)
    setMissings(missings as Array<Partial<PartnerUser> & { from: string }>)
    setDisableDownload(
      size(missings) === total &&
        !isNil(find((item: Mapping) => start === item.from)(missings))
    )
    return missings
  }
  const doDownload = (params: Record<string, string>): void => {
    notify('downloading', { autoHideDuration: null })
    getAllData(partner, params)
      .then(({ data }) => {
        notify('downloading', { autoHideDuration: 0 })
        if (!isNil(data)) {
          const binaryString = window.atob(data)
          const len = binaryString.length
          const bytes = new Uint8Array(len)
          for (let i = 0; i < len; ++i) {
            bytes[i] = binaryString.charCodeAt(i)
          }
          const blob = new Blob([bytes], {
            type: 'application/zip'
          })
          const url = URL.createObjectURL(blob)
          const download = document.createElement('a')
          download.href = url
          download.download = `${format('yyyyMMddHHmmss')(Date.now())}.zip`
          download.click()
          window.URL.revokeObjectURL(url)
        } else {
          notify('no data')
        }
      })
      .catch(log.error)
  }
  const onClickDownload = (): void => {
    const values = getValues()
    const params = reduce(
      (items: any, key: string) =>
        !isNil(values[key]) &&
        ((isBoolean(values[key]) && (values[key] as boolean)) ||
          isNumber(values[key]) ||
          !isEmpty(values[key]))
          ? { ...items, [key]: values[key] }
          : items,
      {}
    )(keys(values))
    assign(params, {
      targets: !(params.all as boolean)
        ? map((item: { key: string }) => item.key)(targets.items)
        : []
    })
    const conditionKey = find(
      (key: string) => /.*_\d+$/.test(key) && (values[key] as boolean)
    )(keys(params))
    if (!isNil(conditionKey) && (params[conditionKey] as boolean)) {
      const index = toNumber(last(split('_')(conditionKey)))
      const { target, kind } = conditions.items[index]
      if (!isEmpty(target)) {
        assign(params, {
          [`conditions_${index}_target`]: target,
          [`conditions_${index}_kind`]: kind
        })
      }
    }
    checkTargets(params)
      .then((missings) => {
        if (!isEmpty(missings)) {
          setParams(params)
          setOpen(true)
        } else {
          doDownload(params)
        }
      })
      .catch(log.error)
  }
  const mainKey = !isEmpty(mappings) ? mappings[0] : undefined
  const schema = z.union([
    z.object(
      reduce(
        (sum: any, key: string) => ({
          ...sum,
          [key]: z.string().nullable().optional()
        }),
        {
          all: z.literal(false),
          [mainKey]: z.string(),
          addressState: z.string().nullable().optional(),
          spinPrefix: z.string().nullable().optional(),
          date: z.string(),
          year: z.string(),
          month: z.string(),
          startTime: z.string(),
          endTime: z.string()
        }
      )(slice(1, size(mappings))(mappings))
    ),
    z.object(
      reduce(
        (sum: any, key: string) => ({
          ...sum,
          [key]: z.string().nullable().optional()
        }),
        {
          all: z.literal(true),
          addressState: z.string().nullable().optional(),
          spinPrefix: z.string().nullable().optional(),
          date: z.string(),
          year: z.string(),
          month: z.string(),
          startTime: z.string(),
          endTime: z.string()
        }
      )(mappings)
    ),
    z.object({
      all: z.literal(true),
      addressState: z.string().nullable().optional(),
      spinPrefix: z.string().nullable().optional(),
      date: z.string(),
      year: z.string(),
      month: z.string(),
      startTime: z.string(),
      endTime: z.string()
    })
  ])
  const { start } = configLimits ?? { start: undefined }
  return (
    <>
      <Grid container>
        <Grid item xs={12} sx={{ mb: '2em' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              gap: '1em'
            }}>
            <Button
              variant="contained"
              onClick={onClickDownload}
              disabled={!schema.safeParse(getValues()).success}>
              {translate('resources.allData.buttons.download')}
            </Button>
            <Button
              variant="outlined"
              onClick={onClickClear}
              disabled={
                isEmpty(
                  filter(
                    (item: { key: string }) =>
                      !isEmpty(item.key) && !isNil(watch()[item.key])
                  )(targets.items)
                ) || disableTargets
              }>
              {translate('resources.allData.buttons.clear')}
            </Button>
            <BooleanInput
              source="all"
              label="resources.allData.fields.all"
              sx={{ mb: '-1.5em' }}
              onChange={onAllSelect}
            />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <Typography variant="h6">{targets.label}</Typography>{' '}
            {!isNil(targets.required) && (targets.required as boolean) ? (
              <Typography variant="h6" color="red">
                （必須）
              </Typography>
            ) : (
              <Typography variant="h6">（任意）</Typography>
            )}
          </Box>
          <Box
            sx={{ display: 'flex', justifyContent: 'flex-start', gap: '1em' }}>
            <FormDataConsumer>
              {({ formData }) => {
                return map(
                  (item: {
                    label: string
                    key: string
                    values: Array<{
                      label: string
                      value: string
                      groups?: string[]
                    }>
                    required?: boolean
                    mappings?: Array<{ label: string; value: string }>
                    groupBy?: string
                  }) => {
                    const { label, key, values, required, mappings, groupBy } =
                      item
                    if (isEmpty(key)) return null
                    const mapTo = (value: string): string => {
                      if (isNil(mappings)) return value
                      const item = find(
                        (item: { label: string; value: string }) =>
                          item.value === value
                      )(mappings)
                      return isNil(item) ? value : item.label
                    }
                    const onSelect = (
                      event: React.ChangeEvent<HTMLInputElement> | RaRecord
                    ): void => {
                      if (isNil(event.target) || isNil(groupBy)) return
                      const { value } = event.target
                      const item = find(
                        (item: {
                          label: string
                          value: string
                          groups?: string[]
                        }) => item.value === value
                      )(values)
                      if (isNil(item)) return
                      const { groups } = item
                      if (isNil(groups)) return
                      // @ts-expect-error ts(2339)
                      map.convert({ cap: false })(
                        (item: string, index: number) => {
                          setValue(item, groups[index])
                        }
                      )(compact(groupBy))
                    }
                    return (
                      <SelectInput
                        key={key}
                        source={key}
                        label={label}
                        choices={compact(
                          map(
                            (item: {
                              label: string
                              value: string
                              groups?: string[]
                            }) => {
                              const { label, value, groups } = item
                              if (isNil(groupBy)) {
                                return {
                                  id: value,
                                  name: mapTo(label)
                                }
                              } else {
                                const grouped = map((item: string) => {
                                  const groupKey = formData[item]
                                  if (
                                    isNil(groupKey) ||
                                    includes(groupKey)(groups)
                                  ) {
                                    return {
                                      id: value,
                                      name: mapTo(label)
                                    }
                                  } else {
                                    return undefined
                                  }
                                })(compact(groupBy))
                                if (
                                  isEmpty(
                                    filter((item: any) => isNil(item))(grouped)
                                  )
                                ) {
                                  return head(grouped)
                                } else {
                                  return undefined
                                }
                              }
                            }
                          )(values)
                        )}
                        validate={
                          !isNil(required) && required ? requiredApi() : []
                        }
                        resettable
                        onChange={onSelect}
                        disabled={disableTargets}
                      />
                    )
                  }
                )(targetValues)
              }}
            </FormDataConsumer>
          </Box>
        </Grid>
        {map(
          (item: {
            label: string
            required?: boolean
            items: Array<{
              label: string
              key: string
              type: string
              defaultValue: string
            }>
            range: string
          }) => {
            const { label, items, range, required } = item
            return (
              <Grid item xs={12} sx={{ mb: 3 }} key={label}>
                <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                  <Typography variant="h6">{label}</Typography>{' '}
                  {!isNil(required) && required ? (
                    <Typography variant="h6" color="red">
                      （必須）
                    </Typography>
                  ) : (
                    <Typography variant="h6">（任意）</Typography>
                  )}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    gap: '1em'
                  }}>
                  {map(
                    (item: {
                      label: string
                      key: string
                      type: string
                      defaultValue: string
                    }) => {
                      const { label, key, type, defaultValue } = item
                      const minDate = parseISO(range)
                      const maxDate = isAfter(
                        parseISO(`${format('yyyy-MM-dd')(Date.now())}T15:00`)
                      )(Date.now())
                        ? subDays(1)(Date.now())
                        : subDays(2)(Date.now())
                      if (
                        type === 'date' ||
                        type === 'month' ||
                        type === 'year'
                      ) {
                        const startDate = endsWith('End')(key)
                          ? watch()[replace('End', '')(key)]
                          : undefined
                        const endDate = !endsWith('End')(key)
                          ? watch()[`${key}End`]
                          : undefined
                        let minDateValue =
                          endsWith('End')(key) && !isNil(startDate)
                            ? parseISO(startDate)
                            : minDate
                        if (
                          !isNil(start) &&
                          isBefore(parseISO(start))(minDateValue)
                        ) {
                          minDateValue = parseISO(start)
                        }
                        return (
                          <DateInput
                            key={key}
                            source={key}
                            label={label}
                            validate={requiredApi()}
                            disableFuture
                            defaultValue={defaultValue ?? null}
                            views={
                              type === 'date'
                                ? ['year', 'month', 'day']
                                : type === 'month'
                                ? ['year', 'month']
                                : ['year']
                            }
                            sx={{ width: 250 }}
                            onChange={onChangeDuration}
                            disabled={endsWith('End')(key) && disableEnd[key]}
                            minDate={
                              type === 'year'
                                ? startOfYear(minDateValue)
                                : minDateValue
                            }
                            maxDate={
                              !endsWith('End')(key) && !isNil(endDate)
                                ? parseISO(endDate)
                                : maxDate
                            }
                          />
                        )
                      }
                      if (type === 'time') {
                        return (
                          <TimeInput
                            key={key}
                            source={key}
                            label={label}
                            validate={requiredApi()}
                            defaultValue={defaultValue ?? null}
                            sx={{ width: 200 }}
                          />
                        )
                      }
                      return null
                    }
                  )(items)}
                </Box>
              </Grid>
            )
          }
        )(durations)}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <Typography variant="h6">{conditions.label}</Typography>{' '}
            {!isNil(conditions.required) && (conditions.required as boolean) ? (
              <Typography variant="h6" color="red">
                （必須）
              </Typography>
            ) : (
              <Typography variant="h6">（任意）</Typography>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              flexDirection: 'column'
            }}>
            {/* @ts-expect-error ts(2339) */}
            {map.convert({ cap: false })(
              (
                item: {
                  label: string
                  valueLabel?: string
                  kind: string
                  annotation?: string
                  target?: string
                },
                index: number
              ) => {
                const { label, valueLabel, kind, annotation, target } = item
                if (!isNil(target) && isEmpty(target)) return null
                if (includes(kind)(['sumRank', 'lastYearRateRank'])) {
                  return (
                    <>
                      <RankConditionInput
                        key={`conditions_${index}`}
                        source={`conditions_${index}`}
                        label={label}
                        valueLabel={valueLabel}
                        rankCondition={rankCondition}
                        setRankCondition={setRankCondition}
                        annotaton={annotation}
                      />
                    </>
                  )
                } else if (kind === 'separateOutput') {
                  return (
                    <OutputConditionInput
                      key={`conditions_${index}`}
                      source={`conditions_${index}`}
                      label={label}
                    />
                  )
                } else {
                  return null
                }
              }
            )(conditions.items)}
          </Box>
        </Grid>
      </Grid>
      <MissingsDialog
        open={open}
        setOpen={setOpen}
        missings={missings}
        doDownload={() => {
          if (!isNil(params)) doDownload(params)
        }}
        disabled={disableDownload}
        config={config}
      />
    </>
  )
}
const RankConditionInput: React.FC<
  {
    valueLabel: string | undefined
    rankCondition: string | undefined
    setRankCondition: (value: string) => void
    annotaton: string | undefined
  } & InputProps
> = ({ valueLabel, rankCondition, setRankCondition, annotaton, ...props }) => {
  const { source, label } = props
  const { field } = useInput(props)
  const { setValue } = useFormContext()
  const conditionValue: number | undefined = useWatch({
    name: `${source}_value`
  })
  const onChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.checked
    setValue(source, value)
    setValue(`${source}_value`, conditionValue)
    if (!isNil(rankCondition)) {
      setValue(rankCondition, false)
    }
    setRankCondition(source)
    field.onChange({ target: { value } })
  }
  return (
    <Grid container>
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          justifyContent: 'flext-start',
          alignItems: 'center'
        }}>
        <Checkbox
          {...field}
          checked={rankCondition === source}
          checkedIcon={<CheckBoxIcon />}
          icon={<CheckBoxOutlineBlankIcon />}
          onChange={onChange}
          name={source}
          disabled={isNil(conditionValue)}
        />
        <Box>
          <Typography>{label}</Typography>
          {!isNil(annotaton) && (
            <Typography variant="caption">{annotaton}</Typography>
          )}
        </Box>
        {!isNil(valueLabel) && (
          <NumberInput
            source={`${source}_value`}
            label={valueLabel}
            min={1}
            sx={{ ml: 2, mb: -2 }}
          />
        )}
      </Grid>
    </Grid>
  )
}
const OutputConditionInput: React.FC<InputProps> = (props) => {
  const { source, label } = props
  const { field } = useInput(props)
  const { value } = field
  const [checkedValue, setCheckedValue] = React.useState<boolean>(
    isBoolean(value) ? value : false
  )
  const { setValue } = useFormContext()
  const onChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.checked
    setCheckedValue(value)
    setValue(source, value)
    field.onChange({ target: { value } })
  }
  return (
    <Grid container>
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          justifyContent: 'flext-start',
          alignItems: 'center'
        }}>
        <Checkbox
          {...field}
          checked={checkedValue}
          checkedIcon={<CheckBoxIcon />}
          icon={<CheckBoxOutlineBlankIcon />}
          onChange={onChange}
          name={source}
        />
        <Typography>{label}</Typography>
      </Grid>
    </Grid>
  )
}
export const AllData: React.FC<{ partner: string } & ConfigProps> = ({
  partner,
  config
}) => {
  const { AllData } = config
  const { data, isLoading, total } = useGetList('users', {
    pagination: { page: 1, perPage: 10 },
    sort: { field: 'id', order: 'ASC' },
    filter: {}
  })
  const [targets, setTargets] = React.useState<any>(undefined)
  const dataProvider = useDataProvider()
  const [users, setUsers] = React.useState<
    Array<Record<string, any>> | undefined
  >(undefined)
  React.useEffect(() => {
    const setupTargets = async (): Promise<void> => {
      if (isLoading || isNil(total) || !isNil(users)) return
      const { data } = await dataProvider.getList('users', {
        pagination: { page: 1, perPage: total },
        sort: { field: 'id', order: 'ASC' },
        filter: {}
      })
      if (isNil(data)) return
      setUsers(data)
      setTargets(
        map((item: { key: string; groupBy?: string }) => {
          const { key, groupBy } = item
          let values = flow([
            groupByApi<any>(key),
            keys,
            uniq,
            map((value: string) => ({ value }))
          ])(data)
          if (!isNil(groupBy)) {
            values = map((item: { value: string }) => {
              const { value } = item
              const samples = filter((item: any) => item[key] === value)(data)
              const groups = flow([
                map((item: any) =>
                  map((key: string) => item[key])(compact(groupBy))
                ),
                flatten
              ])(samples)
              return {
                value,
                groups:
                  groups[0] === '北海道' ? slice(0, 2)(groups) : uniq(groups)
              }
            })(values)
          }
          return {
            ...item,
            values: map((item: { value: string; groups: string[] }) => {
              const { value, groups } = item
              return { label: value, value, groups }
            })(values)
          }
        })(AllData.targets.items)
      )
    }
    setupTargets()
      .then(() => {})
      .catch(log.error)
  }, [data, isLoading, total])
  if (isLoading || isNil(targets)) return <Loading />
  return (
    <Form>
      <FormContent partner={partner} config={config} targetValues={targets} />
    </Form>
  )
}
